<script setup lang="ts">
import Input from '@/components/ui/input/Input.vue'
import Dropdown from '@/components/ui/dropdown/Dropdown.vue'
import Checkbox from '@/components/ui/checkbox/Checkbox.vue'
import Button from '@/components/ui/button/Button.vue'
import { computed, inject, onBeforeMount } from 'vue'
import { NotifiedUserViewModel } from '../viewmodels/NotifiedUser.viewmodel'
import { NotificationChannelEnum } from '@/@core/fire-risk-settings/domain/enums/NotificationChannel.enum'
import HelpTooltip from '@/@web/ui/components/HelpTooltip.vue'
import TelegramLinkQrCode from './TelegramLinkQrCode.vue'

const vm = inject<NotifiedUserViewModel>('notified-user-view-model') as NotifiedUserViewModel

function setPhoneNumber(value: string) {
	vm.notifiedUser.value.phoneNumber = value.replace(/[^0-9]/g, '')
}

const title = computed(() =>
	vm.notifiedUser.value.id
		? 'Editar Usuário de Alerta de Incêndio'
		: 'Cadastrar Usuário de Alerta de Incêndio',
)

onBeforeMount(() => vm.getMonitoredAreas())
</script>

<template>
	<section>
		<h1 class="text-H5">{{ title }}</h1>
		<div class="flex flex-col gap-4 mt-8">
			<Input
				v-model="vm.notifiedUser.value.name"
				label="nome*"
			/>
			<Input
				v-model="vm.notifiedUser.value.email"
				label="e-mail*"
			/>
			<Input
				label="telefone*"
				:value="vm.notifiedUser.value.phoneNumber"
				mask="+## (##) #####-####"
				@input="setPhoneNumber"
			/>

			<Dropdown
				v-model="vm.notifiedUser.value.farms"
				label="fazenda/setor*"
				:options="vm.monitoredAreaOptions.value"
			>
				<template #tooltip>
					<help-tooltip
						title="Fazenda/Setor"
						description="Determine para qual fazenda(s)/setor(es) esse usuário irá receber as notificações de incêndio."
					/>
				</template>
			</Dropdown>

			<div>
				<label class="text-label flex items-center gap-1"
					>canal de mensagens
					<help-tooltip
						title="Canal de mensagens"
						description="Selecione pelo menos um canal de comunicação para notificar o usuário cadastrado com os alertas de incêndio."
					/>
				</label>
				<Checkbox
					:value="vm.notifiedUser.value.channels"
					:model-value="NotificationChannelEnum.EMAIL"
					label="E-mail"
					@input="vm.notifiedUser.value.channels = $event"
				/>
				<Checkbox
					:value="vm.notifiedUser.value.channels"
					:model-value="NotificationChannelEnum.WHATSAPP"
					label="Whatsapp"
					@input="vm.notifiedUser.value.channels = $event"
				/>
				<Checkbox
					:value="vm.notifiedUser.value.channels"
					:model-value="NotificationChannelEnum.TELEGRAM"
					label="Telegram"
					@input="vm.notifiedUser.value.channels = $event"
				/>
			</div>

			<div v-if="vm.showTelegramSection.value">
				<label class="text-label flex gap-1 items-center"
					>link de acesso telegram
					<help-tooltip
						title="Link de acesso telegram"
						:description="`Para receber os alertas via telegram, cada usuário precisa acessar o seu link utilizando a conta que deseja receber as notificações para ativar os alertas.

Cada usuário possui um código de acesso único e imutável que só precisa ser acessado uma única vez.`"
				/></label>

				<telegram-link-qr-code
					class="mt-2"
					v-if="vm.notifiedUser.value.id"
					:user-id="vm.notifiedUser.value.id"
					reverse
				/>
			</div>
		</div>

		<div class="flex gap-4 mt-8">
			<Button
				text="voltar"
				secondary
				:disabled="vm.fetching.value"
				class="w-1/2 justify-center"
				@click="() => (vm.openForm.value = false)"
			/>
			<Button
				v-if="vm.notifiedUser.value.id"
				text="salvar"
				:loading="vm.fetching.value"
				:disabled="!vm.isFormValid.value"
				class="w-1/2 justify-center"
				@click="() => vm.updateNotifiedUser()"
			/>
			<Button
				v-else
				text="cadastrar"
				:loading="vm.fetching.value"
				:disabled="!vm.isFormValid.value"
				class="w-1/2 justify-center"
				@click="() => vm.createNotifiedUser()"
			/>
		</div>
	</section>
</template>

<style scoped>
section {
	padding: 50px 32px 0 50px;
}
</style>
